import { useCallback, useRef, useState } from "react";
import { notch } from "./haptics";

/**
 * Piksler per hakk. Ett hakk per verdi.
 *
 * 20 er riktig for tidskoden: der er det seksti verdier aa komme gjennom, og
 * hjulet maa kunne spinnes. Der det er FAA verdier er det altfor foelsomt —
 * fingeren glir noen piksler i det den slipper, og hjulet har hoppet videre.
 * Se `notchPx`.
 */
const NOTCH = 20;

export interface WheelProps {
  readonly label: string;
  readonly value: string;
  readonly prev: string;
  readonly next: string;
  readonly active: boolean;
  readonly size?: "large" | "small";
  /**
   * Hvor langt fingeren maa dra for ett hakk.
   *
   * Settes opp der verdiene er faa og hver av dem er et valg — en
   * deltakerliste paa fire navn skal ikke blas gjennom, den skal pekes paa.
   * Standard er tidskodens egen.
   */
  readonly notchPx?: number;
  /**
   * Verdiene ETTER naboene, til aa fylle vinduet under en dragning.
   *
   * Uten dem staar en tredel av vinduet tomt naar fingeren naermer seg et
   * hakkskifte. Valgfrie, saa et hjul uten dem fortsatt virker — det blir
   * bare et hull igjen.
   */
  readonly prev2?: string;
  readonly next2?: string;
  onStep(direction: 1 | -1): void;
  onFocus(): void;
}

/**
 * Hjulet fra tidskodearket.
 *
 * Følger fingeren direkte og hakker på hver verdi. Det aktive feltet løftes
 * til `tc`, verdiene over og under falmer mot `ink-faint`. Kan også stilles
 * med et trykk på verdien over eller under den valgte — et trykk på tallet
 * over setter hjulet til nettopp det tallet.
 *
 * Verdien er absolutt, ikke en differanse: man stiller inn en verdi noen
 * sekunder fram i tid og bekrefter i det kilden treffer den. Se
 * docs/13-design.md.
 */
export function Wheel({
  label, value, prev, next, active, size = "large", notchPx = NOTCH,
  prev2 = "", next2 = "", onStep, onFocus,
}: WheelProps): JSX.Element {
  const [offset, setOffset] = useState(0);
  const [settling, setSettling] = useState(false);
  const drag = useRef<{ id: number; y: number; notched: number } | null>(null);
  /*
   * Om fingeren DRO.
   *
   * Naboverdiene er knapper: et trykk paa tallet over setter hjulet dit. Men
   * en knapp gir ogsaa klikk naar en dragning starter og slutter paa den
   * uten aa flytte seg saa mye at nettleseren mister maalet — og da hoppet
   * hjulet ett hakk i det fingeren slapp, uten at noen hadde trykket paa
   * noe. Det var ikke foelsomhet, det var et klikk.
   */
  const dro = useRef(false);

  const big = size === "large" ? 56 : 34;
  const small = size === "large" ? 19 : 15;
  const row = Math.round(big * 0.72);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    // Fangsten holder dragningen selv om fingeren glir utenfor hjulet.
    // Den kan kaste på pekere nettleseren ikke kjenner, og da skal hjulet
    // fortsatt virke — bare uten fangst.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ingen fangst tilgjengelig. Dragningen går videre uansett.
    }
    drag.current = { id: e.pointerId, y: e.clientY, notched: 0 };
    dro.current = false;
    setSettling(false);
    onFocus();
  }, [onFocus]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (d === null || d.id !== e.pointerId) return;
    /*
     * Å dra NEDOVER øker verdien.
     *
     * Hjulet er tegnet med det høyere tallet over det valgte og det lavere
     * under. Å dra nedover flytter kolonnen ned og henter tallet over inn i
     * midten — altså opp én verdi. Gesten og visningen må peke samme vei,
     * ellers står 00 og går til 23 når fingeren sier 01.
     *
     * Merk at prototypen har to hjul som drar hver sin vei: `spin` i
     * tidskodearket øker oppover, `wspin` på timerflatene nedover. Her er
     * alle hjul like, og de følger `wspin`.
     */
    const travel = e.clientY - d.y;
    // Noen piksler er en finger som ligger stille, ikke en dragning.
    if (Math.abs(travel) > 6) dro.current = true;
    const steps = Math.trunc(travel / notchPx);
    while (d.notched < steps) { d.notched += 1; onStep(1); notch(); }
    while (d.notched > steps) { d.notched -= 1; onStep(-1); notch(); }
    setOffset(travel - d.notched * notchPx);
  }, [onStep, notchPx]);

  const finish = useCallback((): void => {
    if (drag.current === null) return;
    drag.current = null;
    setSettling(true);
    setOffset(0);
  }, []);

  const step = (dir: 1 | -1) => (): void => {
    if (dro.current) {
      dro.current = false;
      return;
    }
    onFocus();
    onStep(dir);
    notch();
  };

  /*
   * ALT ER EN FUNKSJON AV AVSTANDEN TIL MIDTEN.
   *
   * Her laa hoppet. Stoerrelsen ble interpolert med tall som ikke traff
   * maalet: naboen vokste til 1,6 ganger sin egen skrift — tretti piksler —
   * og ble saa midtverdien paa femtiseks i det hakket skiftet. Et byks paa
   * aatti prosent i ett bilde, hver gang et navn naermet seg midten.
   *
   * Naa regnes stoerrelse og gjennomsikt av `d`, avstanden fra midten i
   * rader. Den gaar kontinuerlig gjennom hakkskiftet: raden som naar d = 0
   * er NOEYAKTIG saa stor som midtverdien blir i neste bilde, og raden som
   * naar d = 1 er noeyaktig saa stor som naboen blir. Da finnes det ikke noe
   * sprang aa se.
   */
  const t = offset / notchPx;
  const stoerrelse = (d: number): number => big + (small - big) * Math.min(1, Math.abs(d));
  const gjennomsikt = (d: number): number => {
    const a = Math.abs(d);
    return a <= 1 ? 1 - 0.3 * a : Math.max(0, 0.7 - 0.7 * (a - 1));
  };

  /*
   * FEM RADER, IKKE TRE.
   *
   * Vinduet viser tre, og kolonnen flyttes opptil én rad. Med bare tre rader
   * ble en tredel av vinduet TOMT naar man naermet seg et hakkskifte — den
   * neste verdien fantes ikke ennaa. Ytterradene er der bare for aa fylle
   * det hullet mens fingeren er underveis.
   */
  const rader = [
    { v: prev2, o: -2 }, { v: prev, o: -1 }, { v: value, o: 0 },
    { v: next, o: 1 }, { v: next2, o: 2 },
  ] as const;

  return (
    <div className={`wheel${active ? " wheel--active" : ""}`}>
      <div
        className="wheel__window"
        style={{ height: row * 3, width: size === "large" ? 92 : 64 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
      >
        <div
          className={`wheel__column${settling ? " wheel__column--settling" : ""}`}
          /*
           * Midtraden er den tredje, saa kolonnen loeftes én rad. `notchPx`
           * og ikke NOTCH: ett hakk skal flytte den én rad uansett hvor langt
           * fingeren maa dra for hakket.
           */
          style={{ transform: `translateY(${(t - 1) * row}px)` }}
        >
          {rader.map((r) => {
            const d = r.o + t;
            const midt = r.o === 0;
            const ytterst = Math.abs(r.o) === 2;
            return (
              <button
                key={r.o}
                type="button"
                className={midt ? "wheel__value" : "wheel__side"}
                style={{
                  height: row,
                  fontSize: big,
                  transform: `scale(${stoerrelse(d) / big})`,
                  opacity: gjennomsikt(d),
                }}
                onClick={midt ? onFocus : r.o === -1 ? step(1) : r.o === 1 ? step(-1) : undefined}
                /* Fyllradene er ikke kontroller. De finnes for oeyet. */
                tabIndex={ytterst ? -1 : 0}
                aria-hidden={ytterst}
                aria-label={midt ? `${label} ${value}` : r.o === -1 ? `Øk ${label}` : `Senk ${label}`}
              >
                {r.v}
              </button>
            );
          })}
        </div>
      </div>
      <span className="wheel__label">{label}</span>
    </div>
  );
}
